import React, { useEffect, useState, useRef } from 'react';
import { Check, Package, DollarSign } from 'lucide-react';

// TODO: Bug crítico - El contador de entregas/pagos se incrementa múltiples veces cuando no debería.
// Problemas identificados:
// 1. El contador se incrementa más veces de las necesarias
// 2. La lógica actual con useRef no está previniendo el comportamiento
// 3. Posible problema con el estado y las dependencias del useEffect
// Necesita revisión urgente para asegurar que el contador solo se incremente una vez por entrega/pago.

const VARIANTS = {
  delivery: {
    icon: Package,
    bgColor: 'bg-emerald-500',
    title: '¡Entrega completada!',
    countText: 'entregas realizadas hoy',
    confettiSymbol: '📦'
  },
  payment: {
    icon: DollarSign,
    bgColor: 'bg-blue-500',
    title: '¡Pago registrado!',
    countText: 'cobros realizados hoy',
    confettiSymbol: '💸'
  }
};

// Función para obtener la fecha actual en formato YYYY-MM-DD
const getTodayKey = () => new Date().toISOString().split('T')[0];

// Función para obtener el contador actual del localStorage
const getStoredCount = (variant) => {
  try {
    const stored = localStorage.getItem(`${variant}_count`);
    if (!stored) return 0;
    
    const { date, count } = JSON.parse(stored);
    // Si es un nuevo día, reiniciar el contador
    if (date !== getTodayKey()) return 0;
    
    return count;
  } catch (error) {
    console.error('Error reading count from localStorage:', error);
    return 0;
  }
};

// Función para guardar el contador en localStorage
const storeCount = (variant, count) => {
  try {
    localStorage.setItem(`${variant}_count`, JSON.stringify({
      date: getTodayKey(),
      count
    }));
  } catch (error) {
    console.error('Error storing count in localStorage:', error);
  }
};

export function SuccessScreen({ show, variant = 'delivery', onClose }) {
  const [count, setCount] = useState(() => getStoredCount(variant));
  const showRef = useRef(show);
  const config = VARIANTS[variant];
  const Icon = config.icon;
  
  // Efecto para manejar el contador
  useEffect(() => {
    // Solo incrementamos si show cambia de false a true
    if (show && !showRef.current) {
      const currentCount = getStoredCount(variant); // Obtenemos el valor actual directamente
      const newCount = currentCount + 1;
      setCount(newCount);
      storeCount(variant, newCount);
    }
    showRef.current = show;
  }, [show, variant]);

  // Efecto separado para el auto-close
  useEffect(() => {
    let timer;
    if (show) {
      timer = setTimeout(() => {
        onClose?.();
      }, 2500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [show, onClose]);

  // Componente de confetti simplificado y específico por variante
  const Confetti = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className={`
            absolute left-1/2 top-1/2 
            h-6 w-6 
            flex items-center justify-center
            transition-all duration-1200 ease-out
            ${show ? 'scale-100 opacity-0' : 'scale-0 opacity-100'}
          `}
          style={{
            transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-150px)`,
            transitionDelay: `${i * 80}ms`
          }}
        >
          {config.confettiSymbol}
        </div>
      ))}
    </div>
  );

  return (
    <div 
      className={`fixed inset-0 flex items-center justify-center transition-all duration-700 z-50
        ${show ? `opacity-100 ${config.bgColor}` : 'opacity-0 pointer-events-none'}`}
      onClick={() => onClose?.()}
    >
      
      <Confetti />
      
      {/* Efecto de pulso suave */}
      <div className={`absolute w-64 h-64 bg-white/10 rounded-full transition-transform duration-1200 
        ${show ? 'scale-150 opacity-0' : 'scale-100 opacity-100'}`} />
      
      <div className="relative flex flex-col items-center">
        {/* Contenedor del ícono con animación */}
        <div className="relative mb-8">
          <Icon 
            className={`absolute left-1/2 -translate-x-1/2 text-white transition-all duration-700
              ${show ? '-translate-y-16 opacity-0 scale-75' : 'translate-y-0 opacity-100 scale-100'}`} 
            size={72} 
          />
          <Check 
            className={`text-white transition-all duration-700 
              ${show ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} 
            size={72} 
          />
        </div>
        
        {/* Mensaje principal */}
        <h2 className={`text-white text-4xl font-bold mb-6 transition-all duration-700 
          ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          {config.title}
        </h2>
        
        {/* Contador */}
        <div className={`transition-all duration-700 delay-150 
          ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          <div className="bg-white/20 px-6 py-3 rounded-full">
            <p className="text-white text-2xl">
              {count} {config.countText}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 